import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Modal, { ModalContent, ModalFooter } from '../../components/ui/Modal';
import FormInput from '../../components/ui/FormInput';

interface User {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  isAdmin: boolean;
  isModerator?: boolean;
  isSupport?: boolean;
  isShadowbanned?: boolean;
  createdAt: string;
  lastLoginAt?: string;
  emailVerified: boolean;
  creatorProfile?: any;
  patronAccounts?: any[];
}

const AdminUsersPage: React.FC = () => {
  const [items, setItems] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<'admin'|'creator'|'patron'|'all'>('all');
  const [status, setStatus] = useState<'active'|'suspended'|'all'>('all');
  const [showShadowbanModal, setShowShadowbanModal] = useState<{ userId: string; userName: string } | null>(null);
  const [shadowbanReason, setShadowbanReason] = useState('');

  const load = async () => {
    setLoading(true);
    const res = await adminService.listUsers({ query, role, status, limit: 50 });
    setItems(res.users || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const suspend = async (id: string) => { await adminService.suspendUser(id); await load(); };
  const activate = async (id: string) => { await adminService.activateUser(id); await load(); };
  const toggleAdmin = async (id: string, isAdmin: boolean) => { await adminService.setAdmin(id, !isAdmin); await load(); };
  const toggleModerator = async (id: string, isModerator: boolean) => { await adminService.setModerator(id, !isModerator); await load(); };
  const toggleSupport = async (id: string, isSupport: boolean) => { await adminService.setSupport(id, !isSupport); await load(); };

  const handleShadowban = async () => {
    if (!showShadowbanModal || !shadowbanReason.trim()) return;
    await adminService.shadowbanUser(showShadowbanModal.userId, shadowbanReason);
    setShowShadowbanModal(null);
    setShadowbanReason('');
    await load();
  };

  const handleUnshadowban = async (id: string) => {
    await adminService.unshadowbanUser(id);
    await load();
  };

  const getUserRole = (user: User) => {
    if (user.isAdmin) return 'Admin';
    if (user.isModerator) return 'Moderator';
    if (user.isSupport) return 'Support';
    if (user.creatorProfile) return 'Creator';
    if (user.patronAccounts?.length) return 'Patron';
    return 'User';
  };

  const getUserStatus = (user: User) => {
    if (user.isShadowbanned) return 'Shadowbanned';
    return user.isActive ? 'Active' : 'Suspended';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Users</h1>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="Search email/username" className="px-3 py-2 border rounded" />
        <select value={role} onChange={(e)=>setRole(e.target.value as any)} className="px-3 py-2 border rounded">
          <option value="all">All roles</option>
          <option value="admin">Admins</option>
          <option value="creator">Creators</option>
          <option value="patron">Patrons</option>
        </select>
        <select value={status} onChange={(e)=>setStatus(e.target.value as any)} className="px-3 py-2 border rounded">
          <option value="all">All status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <Button onClick={load}>Search</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td className="px-4 py-6" colSpan={5}>Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td className="px-4 py-6" colSpan={5}>No users found.</td></tr>
                ) : items.map(u => (
                  <tr key={u.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{u.username}</div>
                      <div className="text-sm text-gray-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          getUserRole(u).includes('Admin') ? 'bg-red-100 text-red-800' :
                          getUserRole(u).includes('Moderator') ? 'bg-blue-100 text-blue-800' :
                          getUserRole(u).includes('Support') ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getUserRole(u)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        getUserStatus(u) === 'Active' ? 'bg-green-100 text-green-800' :
                        getUserStatus(u) === 'Suspended' ? 'bg-red-100 text-red-800' :
                        getUserStatus(u) === 'Shadowbanned' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getUserStatus(u)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 space-x-2">
                      <div className="flex flex-wrap gap-1">
                        <Button size="sm" variant="outline" onClick={()=>toggleAdmin(u.id, u.isAdmin)}>{u.isAdmin ? 'Revoke Admin' : 'Make Admin'}</Button>
                        <Button size="sm" variant="outline" onClick={()=>toggleModerator(u.id, u.isModerator || false)} className={u.isModerator ? 'bg-blue-50 border-blue-200' : ''}>
                          {u.isModerator ? 'Remove Mod' : 'Make Mod'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={()=>toggleSupport(u.id, u.isSupport || false)} className={u.isSupport ? 'bg-green-50 border-green-200' : ''}>
                          {u.isSupport ? 'Remove Support' : 'Make Support'}
                        </Button>
                        {u.isShadowbanned ? (
                          <Button size="sm" variant="outline" onClick={()=>handleUnshadowban(u.id)} className="bg-yellow-50 border-yellow-200">
                            Unshadowban
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={()=>setShowShadowbanModal({ userId: u.id, userName: u.username })} className="bg-yellow-50 border-yellow-200">
                            Shadowban
                          </Button>
                        )}
                        {u.isActive ? (
                          <Button size="sm" variant="danger" onClick={()=>suspend(u.id)}>Suspend</Button>
                        ) : (
                          <Button size="sm" onClick={()=>activate(u.id)}>Activate</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Shadowban Modal */}
      {showShadowbanModal && (
        <Modal isOpen={!!showShadowbanModal} onClose={() => setShowShadowbanModal(null)}>
          <ModalContent>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Shadowban User</h3>
            <p className="text-sm text-gray-600 mb-4">
              Shadowbanning <strong>{showShadowbanModal.userName}</strong> will hide their content from other users while allowing them to continue using the platform unaware of the restriction.
            </p>
            <FormInput
              label="Reason for shadowban (required)"
              type="textarea"
              value={shadowbanReason}
              onChange={(e) => setShadowbanReason(e.target.value)}
              placeholder="Please provide a reason for this shadowban..."
              required
            />
          </ModalContent>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowShadowbanModal(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleShadowban}
              disabled={!shadowbanReason.trim()}
            >
              Shadowban User
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default AdminUsersPage;



